import { SyntheticEvent, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import Footer from "../../SharedModule/components/Footer";
import { verifyModuleAccess } from "../../SharedModule/utils/accessUtils";
import { Spinner } from "../../SharedModule/components/Spinner";
import {
  CLIENTS_ALL_ROLES,
  ROUTE_CLIENTS,
  ROUTE_CLIENTS_WORK,
  ROUTE_CLIENTS_RELATIONSHIP_REVIEW,
} from "../../SharedModule/utils/constants";
import { ForbiddenPage } from "../../SharedModule/pages/ForbiddenPage";
import { useLocation, useNavigate, useParams } from "react-router";
import { setActiveTab } from "../../SharedModule/redux/reducers/uiShared.reducer";
import HeaderClientsModule from "../components/HeaderClientsModule";
import { useClientWork } from "../hooks/useClientWork";
import { AcordionRecurringBillingsTable } from "../components/AcordionRecurringBillingsTable";
import { AcordionWorkOrdersTable } from "../components/AcordionWorkOrdersTable";
import { AcordionProjectsTable } from "../components/AcordionProjectsTable";
import { NoContent } from "../../SharedModule/components/NoContent";
import image from "./../../styles/legacy/404.png";

// Acordion imports
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { styled } from "@mui/material/styles";
import MuiAccordion, { AccordionProps } from "@mui/material/Accordion";
import MuiAccordionDetails from "@mui/material/AccordionDetails";
import MuiAccordionSummary, {
  AccordionSummaryProps,
  accordionSummaryClasses,
} from "@mui/material/AccordionSummary";
import { resetPageFilters } from "../redux/reducers/clientWork.reducer";
import { resetScrollY } from "../../SharedModule/utils/pagesUtils";

export const ClientWorkPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { id } = useParams();

  // If there is no id, go to clients list
  if (!id) {
    navigate(ROUTE_CLIENTS);
  }

  const {
    isLoadingWorkInfo,
    clientRecurringBillings,
    clientWorkOrders,
    clientProjects,
    userRolesList,
    rolesLoaded,
  } = useClientWork(Number(id));

  useEffect(() => {
    document.title = "The Hub - Client Work";

    dispatch(setActiveTab(ROUTE_CLIENTS_WORK));

    return () => {
      resetScrollY(globalThis.location.pathname);
      dispatch(resetPageFilters());
    };
  }, []);

  const Accordion = styled((props: AccordionProps) => (
    <MuiAccordion disableGutters elevation={0} {...props} />
  ))(({ theme }) => ({
    border: `1px solid ${theme.palette.divider}`,
    "&:not(:last-child)": {
      borderBottom: 0,
    },
    "&::before": {
      display: "none",
    },
  }));

  const AccordionSummary = styled((props: AccordionSummaryProps) => (
    <MuiAccordionSummary {...props} />
  ))(({ theme }) => ({
    backgroundColor: "#eeecf5",
    flexDirection: "row",
    [`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]:
      {
        transform: "rotate(180deg)",
      },
    [`& .${accordionSummaryClasses.content}`]: {
      marginLeft: theme.spacing(1),
    },
    ...theme.applyStyles("dark", {
      backgroundColor: "rgba(255, 255, 255, .05)",
    }),
  }));

  const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
    padding: theme.spacing(2),
    borderTop: "1px solid rgba(0, 0, 0, .125)",
  }));

  let accordionTabDefaultExpanded: string | false = false;
  if (clientRecurringBillings.length > 0) {
    accordionTabDefaultExpanded = "panel1";
  } else if (clientWorkOrders.length > 0) {
    accordionTabDefaultExpanded = "panel2";
  } else if (clientProjects.length > 0) {
    accordionTabDefaultExpanded = "panel3";
  }

  const [expanded, setExpanded] = useState<string | false>(
    accordionTabDefaultExpanded
  );

  useEffect(() => {
    setExpanded(accordionTabDefaultExpanded);
  }, [isLoadingWorkInfo]);

  const handleChange =
    (panel: string) => (event: SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  const checkNoContent = () => {
    return (
      (!clientRecurringBillings || clientRecurringBillings.length === 0) &&
      (!clientWorkOrders || clientWorkOrders.length === 0) &&
      (!clientProjects || clientProjects.length === 0)
    );
  };

  return (
    <div className="content">
      <HeaderClientsModule
        isLoadingPage={isLoadingWorkInfo || !rolesLoaded}
        backFromRelationshipPage={
          location.state?.from === ROUTE_CLIENTS_RELATIONSHIP_REVIEW
        }
      />

      {(isLoadingWorkInfo || !rolesLoaded) && (
        <Spinner
          style={{
            marginLeft: "50%",
            marginTop: "5%",
          }}
        />
      )}

      {!isLoadingWorkInfo &&
        rolesLoaded &&
        !verifyModuleAccess(true, CLIENTS_ALL_ROLES, userRolesList) && (
          <ForbiddenPage />
        )}

      {!isLoadingWorkInfo &&
        rolesLoaded &&
        verifyModuleAccess(true, CLIENTS_ALL_ROLES, userRolesList) && (
          <>
            {checkNoContent() && (
              <NoContent
                title="No Work for this Client"
                image={image}
                style={{ margin: "3% auto" }}
              />
            )}

            {!checkNoContent() && (
              <div className="mt-4">
                <Accordion
                  expanded={expanded === "panel1"}
                  onChange={handleChange("panel1")}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel1-content"
                    id="panel1-header"
                  >
                    <Typography className="acordion-title">
                      Recurring Services
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <AcordionRecurringBillingsTable
                      clientRecurringBillings={clientRecurringBillings}
                    />
                  </AccordionDetails>
                </Accordion>

                <Accordion
                  expanded={expanded === "panel2"}
                  onChange={handleChange("panel2")}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel2-content"
                    id="panel2-header"
                  >
                    <Typography className="acordion-title">
                      Work Orders
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <AcordionWorkOrdersTable
                      clientWorkOrders={clientWorkOrders}
                    />
                  </AccordionDetails>
                </Accordion>

                <Accordion
                  expanded={expanded === "panel3"}
                  onChange={handleChange("panel3")}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel3-content"
                    id="panel3-header"
                  >
                    <Typography className="acordion-title">Projects</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <AcordionProjectsTable clientProjects={clientProjects} />
                  </AccordionDetails>
                </Accordion>
              </div>
            )}
          </>
        )}
      <Footer />
    </div>
  );
};
